import { Injectable } from '@angular/core';
import {
    INotesComponent,
    INotesSection
} from '@modeso/types__aumico-lib-annualreport-be';
import { ComponentType } from '@modeso/types__aumico-lib-notes-be';
import { cloneDeep } from 'lodash-es';
import { INotesComponentsNumberedTitles } from '../interfaces/notes-numbered-titles.interface';
import { ComponentsEmptyDeterminatorHelper } from './componentsEmptyDeterminator.helper';
import { NotesEmptyLinesRemovalHelper } from './notesEmptyLinesRemoval.helper';

@Injectable({ providedIn: 'root' })
export class NotesNumberingHelper {
    constructor(private notesEmptyLinesRemovalHelper: NotesEmptyLinesRemovalHelper, private componentsEmptyDeterminatorHelper: ComponentsEmptyDeterminatorHelper) { }

    public createNumberingObject(
        notesSectionsWithNumbering: INotesSection[],
        language: string,
    ): Array<Array<INotesComponentsNumberedTitles>> {
        const numberedTitlesObject: Array<Array<INotesComponentsNumberedTitles>> = [];
        for (const notesSectionWithNumbering of notesSectionsWithNumbering) {
            const sectionNumbering: Array<INotesComponentsNumberedTitles> = [];
            for (const componentWithNumbering of notesSectionWithNumbering.data) {
                if (componentWithNumbering.isHidden) {
                    const { title: componentTitle } = this.getComponentTitleAndNumbering(
                        componentWithNumbering,
                        language,
                    );
                    sectionNumbering.push({
                        componentId: componentWithNumbering.componentId,
                        componentTitle: componentTitle,
                        componentNumber: '',
                        tableNumbers: [],
                    });
                    continue;
                }
                const { numbering: componentNumber, title: componentTitle } = this.getComponentTitleAndNumbering(
                    componentWithNumbering,
                    language,
                );
                const componentNumbering: INotesComponentsNumberedTitles = {
                    componentId: componentWithNumbering.componentId,
                    componentTitle: componentTitle,
                    componentNumber: componentNumber,
                    tableNumbers: [],
                };

                if (componentWithNumbering.componentName === ComponentType.EQUITY) {
                    const title = componentWithNumbering.data.options.textTitle?.[language];
                    const { numbering: tableNumber } = this.extractNumberingAndTitle(title);

                    componentNumbering.tableNumbers.push(tableNumber);
                }

                if (
                    componentWithNumbering.componentName === ComponentType.FREE_TABLE_WITH_AMOUNTS ||
                    componentWithNumbering.componentName === ComponentType.SELECT ||
                    componentWithNumbering.componentName === ComponentType.SUPER_TABLE
                ) {
                    const title = componentWithNumbering.data.options.title?.[language];
                    const { numbering: tableNumber } = this.extractNumberingAndTitle(title);

                    componentNumbering.tableNumbers.push(tableNumber);
                }

                if (
                    componentWithNumbering.componentName === ComponentType.FINANCIAL_LINE_ITEM &&
                    componentWithNumbering.data.flis != null
                ) {
                    for (const tableWithNumbering of componentWithNumbering.data.flis) {
                        if (tableWithNumbering == undefined) {
                            continue;
                        }
                        const title = tableWithNumbering.caption;
                        const { numbering: tableNumber } = this.extractNumberingAndTitle(title);

                        componentNumbering.tableNumbers.push(tableNumber);
                    }
                }

                if (
                    componentWithNumbering.componentName === ComponentType.MOVEMENT_SCHEDULE &&
                    componentWithNumbering.data.flis != null
                ) {
                    for (const tableWithNumbering of componentWithNumbering.data.flis) {
                        if (tableWithNumbering == undefined) {
                            continue;
                        }
                        const title = tableWithNumbering.caption?.[language];
                        const { numbering: tableNumber } = this.extractNumberingAndTitle(title);

                        componentNumbering.tableNumbers.push(tableNumber);
                    }
                }

                sectionNumbering.push(componentNumbering);
            }

            numberedTitlesObject.push(sectionNumbering);
        }
        return numberedTitlesObject;
    }

    private getComponentTitleAndNumbering(
        component: INotesComponent,
        language: string,
    ): { numbering: string; title: string } {
        if (component.componentTitle) {
            return this.extractNumberingAndTitle(component.componentTitle[language]);
        }

        // For the users that previously saved titles in these properties as component titles

        if (component.componentName === ComponentType.RICH_TEXT && component.data.title != null) {
            return this.extractNumberingAndTitle(component.data.title[language]);
        }

        return { numbering: '', title: '' };
    }

    public extractNumberingAndTitle(text: string | undefined, language?: string): { numbering: string; title: string } {
        if (text == null) {
            return { numbering: '', title: '' };
        }

        // If localized map, pick current language (or fallback)
        if (typeof text === 'object') {
            text =
                (text && text[language]) ??
                (text && text[language?.toLowerCase()]) ??
                (text && text[language?.toUpperCase()]) ??
                (text && Object.values(text).find((v) => typeof v === 'string')) ??
                '';
        }

        // Ensure it's a string before match
        const str = typeof text === 'string' ? text : String(text);

        const match = str.match(/^([\d.]+)\s+(.*)$/);
        if (match) {
            let [, numbering, title] = match;
            if (title === 'undefined') {
                title = '';
            }

            return { numbering, title };
        }
        return { numbering: '', title: text };
    }

    public applyNumbering(
        notesSections: INotesSection[],
        language: string,
        removeEmptyData: boolean,
        fallbackLanguage?: string,
    ): INotesSection[] {
        const notesSectionsWithNumbering = cloneDeep<INotesSection[]>(notesSections);
        let sectionCounter = 1;

        for (const section of notesSectionsWithNumbering) {
            const sectionHasAtleastOneNonEmptyComponent = this.processSection(
                section,
                sectionCounter,
                language,
                removeEmptyData,
                fallbackLanguage
            );

            if (sectionHasAtleastOneNonEmptyComponent) {
                sectionCounter++;
            }
        }

        return notesSectionsWithNumbering;
    }


    /**
     * Process a single section with numbering
     */
    private processSection(
        section: INotesSection,
        sectionCounter: number,
        language: string,
        removeEmptyData: boolean,
        fallbackLanguage?: string
    ): boolean {
        let sectionHasAtleastOneNonEmptyComponent = false;
        section.title[language] = section.title[language] || section.title[fallbackLanguage];

        let lastComponentNumber: string | null = null;
        let lastTableNumber: string | null = null;
        let lastComponentHadNumbering = false;

        for (const component of section.data) {
            if (component.isHidden) {
                continue;
            }
            if (removeEmptyData && this.componentsEmptyDeterminatorHelper.isComponentConsideredEmpty(component.componentName, component.data, language)) {
                continue;
            }

            sectionHasAtleastOneNonEmptyComponent = true;

            const result = this.processComponent(
                component,
                sectionCounter,
                lastComponentNumber,
                lastTableNumber,
                lastComponentHadNumbering,
                language,
                fallbackLanguage
            );

            lastComponentNumber = result.lastComponentNumber;
            lastTableNumber = result.lastTableNumber;
            lastComponentHadNumbering = result.componentHadNumbering;
        }

        return sectionHasAtleastOneNonEmptyComponent;
    }

    /**
     * Process a single component with numbering
     */
    private processComponent(
        component: INotesComponent,
        sectionCounter: number,
        lastComponentNumber: string | null,
        lastTableNumber: string | null,
        lastComponentHadNumbering: boolean,
        language: string,
        fallbackLanguage?: string
    ): { lastComponentNumber: string | null; lastTableNumber: string | null; componentHadNumbering: boolean } {
        const noComponentNumbering = this.shouldSkipComponentNumbering(component, language);

        // Apply component title numbering

        if (!noComponentNumbering && lastComponentNumber == null) {
            // If this is the first numbered component, start from last table number if exists
            lastComponentNumber = lastTableNumber;
        }

        let updatedLastComponentNumber = this.applyComponentTitleNumbering(
            component,
            sectionCounter,
            lastComponentNumber,
            noComponentNumbering,
            language,
            fallbackLanguage
        );

        // If this component had numbering, we should NOT reset the table numbering context
        // to allow tables within the same component to increment properly
        const componentHadNumbering = !noComponentNumbering;
        let effectiveLastTableNumber = lastTableNumber;

        // Process component data (tables, FLIs, etc.)
        if (component.data) {

            if (!noComponentNumbering && lastTableNumber == null && effectiveLastTableNumber == null) {
                // If this is the first numbered table, start from last component number if exists
                lastTableNumber = updatedLastComponentNumber;
            }

            const result = this.processComponentData(
                component,
                sectionCounter,
                updatedLastComponentNumber,
                effectiveLastTableNumber,
                lastComponentHadNumbering,
                noComponentNumbering,
                language,
                fallbackLanguage
            );

            lastTableNumber = result.lastTableNumber;
            updatedLastComponentNumber = result.lastComponentNumber;
        }

        return {
            lastComponentNumber: updatedLastComponentNumber,
            lastTableNumber,
            componentHadNumbering
        };
    }

    private shouldSkipComponentNumbering(component: INotesComponent, language: string): boolean {
        return component.componentTitle == null ||
            !component.addNumbering ||
            component.componentTitle[language] === '';
    }

    private applyComponentTitleNumbering(
        component: INotesComponent,
        sectionCounter: number,
        lastComponentNumber: string | null,
        noComponentNumbering: boolean,
        language: string,
        fallbackLanguage?: string
    ): string | null {
        if (!noComponentNumbering) {
            // Increment component counter based on last used component number
            const finalComponentNumber = this.getNextComponentNumber(sectionCounter, lastComponentNumber);

            component.componentTitle[language] = `${finalComponentNumber} ${component.componentTitle[language] || component.componentTitle[fallbackLanguage]
                }`;

            return finalComponentNumber;
        }

        return lastComponentNumber;
    }

    /**
     * Process all data within a component (tables, FLIs, etc.)
     */
    private processComponentData(
        component: INotesComponent,
        sectionCounter: number,
        lastComponentNumber: string | null,
        lastTableNumber: string | null,
        lastComponentHadNumbering: boolean,
        noComponentNumbering: boolean,
        language: string,
        fallbackLanguage?: string
    ): { lastTableNumber: string | null; lastComponentNumber: string | null } {
        let updatedLastTableNumber = lastTableNumber;
        let updatedLastComponentNumber = lastComponentNumber;

        if (!noComponentNumbering) {
            // if component has numbering, reset last table number
            lastTableNumber = null;
            updatedLastTableNumber = null;
        }

        // Handle table components
        if ((component.componentName === ComponentType.FREE_TABLE_WITH_AMOUNTS ||
            component.componentName === ComponentType.SELECT ||
            component.componentName === ComponentType.SUPER_TABLE) && component.data.addNumbering) {

            updatedLastTableNumber = this.processTableComponent(
                component,
                sectionCounter,
                lastComponentNumber,
                lastTableNumber,
                lastComponentHadNumbering,
                noComponentNumbering,
                language,
                fallbackLanguage
            );


        }

        // Handle Financial Line Items
        else if (component.componentName === ComponentType.FINANCIAL_LINE_ITEM) {
            updatedLastTableNumber = this.processFinancialLineItems(
                component,
                sectionCounter,
                lastComponentNumber,
                lastTableNumber,
                lastComponentHadNumbering,
                noComponentNumbering,
                language
            );


        }

        // Handle Movement Schedules
        else if (component.componentName === ComponentType.MOVEMENT_SCHEDULE) {
            updatedLastTableNumber = this.processMovementSchedule(
                component,
                sectionCounter,
                lastComponentNumber,
                lastTableNumber,
                lastComponentHadNumbering,
                noComponentNumbering,
                language,
                fallbackLanguage
            );


        }

        // Handle Equity tables
        else if (component.componentName === ComponentType.EQUITY && component.data.addNumbering) {
            updatedLastTableNumber = this.processEquityTable(
                component,
                sectionCounter,
                lastComponentNumber,
                lastTableNumber,
                lastComponentHadNumbering,
                noComponentNumbering,
                language,
                fallbackLanguage
            );


        }

        // Handle Rich Text since it has no table number
        else if (component.componentName === ComponentType.RICH_TEXT && !noComponentNumbering) {
            updatedLastTableNumber = null;

        }

        return {
            lastTableNumber: updatedLastTableNumber,
            lastComponentNumber: updatedLastComponentNumber
        };
    }

    /**
     * Process table components (SUPER_TABLE, FREE_TABLE_WITH_AMOUNTS, SELECT)
     */
    private processTableComponent(
        component: INotesComponent,
        sectionCounter: number,
        lastComponentNumber: string | null,
        lastTableNumber: string | null,
        lastComponentHadNumbering: boolean,
        noComponentNumbering: boolean,
        language: string,
        fallbackLanguage?: string
    ): string | null {
        if (this.componentsEmptyDeterminatorHelper.isTableDataEmpty(component.componentName, component.data)) {
            return lastTableNumber;
        }

        if (!this.componentsEmptyDeterminatorHelper.isPropertyEmpty(component.data.options.title)) {
            const tableNumber = this.calculateTableNumber(
                sectionCounter,
                lastComponentNumber,
                lastTableNumber,
                lastComponentHadNumbering,
                noComponentNumbering
            );

            component.data.options.title[language] = `${tableNumber} ${component.data.options.title[language] || component.data.options.title[fallbackLanguage]
                }`;

            return tableNumber;
        }

        return lastTableNumber;
    }

    /**
     * Process Financial Line Items
     */
    private processFinancialLineItems(
        component: INotesComponent,
        sectionCounter: number,
        lastComponentNumber: string | null,
        lastTableNumber: string | null,
        lastComponentHadNumbering: boolean,
        noComponentNumbering: boolean,
        language: string
    ): string | null {
        let currentLastTableNumber = lastTableNumber;

        component.data.flis = component.data.flis.map(fli => {
            if (fli === undefined || fli.addNumbering === false || this.componentsEmptyDeterminatorHelper.isFliTableDataEmpty(fli)) {
                return fli;
            }

            const fliNumber = this.calculateTableNumber(
                sectionCounter,
                lastComponentNumber,
                currentLastTableNumber,
                lastComponentHadNumbering,
                noComponentNumbering
            );

            const newFli = cloneDeep(fli);
            const { title: originalTitle } = this.extractNumberingAndTitle(newFli.caption, language);
            newFli.caption = `${fliNumber} ${originalTitle}`;

            currentLastTableNumber = fliNumber;
            return newFli;
        });

        return currentLastTableNumber;
    }

    /**
     * Process Movement Schedule
     */
    private processMovementSchedule(
        component: INotesComponent,
        sectionCounter: number,
        lastComponentNumber: string | null,
        lastTableNumber: string | null,
        lastComponentHadNumbering: boolean,
        noComponentNumbering: boolean,
        language: string,
        fallbackLanguage?: string
    ): string | null {
        let currentLastTableNumber = lastTableNumber;

        component.data.flis = component.data.flis.map(fli => {
            if (fli == undefined || fli.addNumbering === false || this.componentsEmptyDeterminatorHelper.isMovementScheduleFliTableDataEmpty(fli)) {
                return fli;
            }

            const fliNumber = this.calculateTableNumber(
                sectionCounter,
                lastComponentNumber,
                currentLastTableNumber,
                lastComponentHadNumbering,
                noComponentNumbering
            );

            const newFli = cloneDeep(fli);
            const { title: originalTitle } = this.extractNumberingAndTitle(
                newFli.caption[language] || newFli.caption[fallbackLanguage],
            );
            newFli.caption[language] = `${fliNumber} ${originalTitle}`;

            currentLastTableNumber = fliNumber;
            return newFli;
        });

        return currentLastTableNumber;
    }

    /**
     * Process Equity table
     */
    private processEquityTable(
        component: INotesComponent,
        sectionCounter: number,
        lastComponentNumber: string | null,
        lastTableNumber: string | null,
        lastComponentHadNumbering: boolean,
        noComponentNumbering: boolean,
        language: string,
        fallbackLanguage?: string
    ): string | null {
        if (this.componentsEmptyDeterminatorHelper.isTableDataEmpty(component.componentName, component.data)) {
            return lastTableNumber;
        }

        const tableNumber = this.calculateTableNumber(
            sectionCounter,
            lastComponentNumber,
            lastTableNumber,
            lastComponentHadNumbering,
            noComponentNumbering
        );

        if (component.data.options.textTitle != null) {
            component.data.options.textTitle[language] = `${tableNumber} ${component.data.options.textTitle[language] ||
                component.data.options.textTitle[fallbackLanguage]
                }`;
        }

        return tableNumber;
    }


    private calculateTableNumber(
        sectionCounter: number,
        lastComponentNumber: string | null,
        lastTableNumber: string | null,
        lastComponentHadNumbering: boolean,
        noComponentNumbering: boolean
    ): string {
        if (noComponentNumbering && lastTableNumber && !lastComponentHadNumbering) {
            // Component has addNumbering=false and last component also had no numbering: 
            // increment based on last table number
            return this.getNextTableNumber(lastTableNumber);
        } else if (noComponentNumbering && lastTableNumber && lastComponentHadNumbering) {
            // Component has addNumbering=false but last component had numbering and there's a previous table:
            // Increment the 3-level numbering (e.g., 1.2.1 -> 1.2.2)
            return this.getNextTableNumber(lastTableNumber);
        } else if (noComponentNumbering && lastComponentNumber) {
            // Component has addNumbering=false, last component had numbering, but no previous table: 
            // Start 3-level numbering based on the last component number (e.g., 1.2 -> 1.2.1)
            return `${lastComponentNumber}.1`;
        } else if (noComponentNumbering) {
            // Component has addNumbering=false and no previous component number: 
            // Use section-level numbering
            return `${sectionCounter}.1`;
        } else {
            // Component has addNumbering=true: use 3-level numbering (component.table)
            const componentPart = lastComponentNumber || `${sectionCounter}.0`;

            // If we have a last table number and it belongs to the current component, increment it
            if (lastTableNumber && lastTableNumber.startsWith(componentPart + '.')) {
                return this.getNextTableNumber(lastTableNumber);
            } else {
                // First table in this component
                return `${componentPart}.1`;
            }
        }
    }


    /**
     * Increments the last table number based on its level
     * If 2 level (e.g., "2.1") -> increment second digit -> "2.2"
     * If 3 level (e.g., "2.1.3") -> increment third digit -> "2.1.4"
     */
    private getNextTableNumber(lastTableNumber: string): string {
        const parts = lastTableNumber.split('.');

        if (parts.length === 2) {
            // 2 level numbering: increment second digit
            const lastDigit = parseInt(parts[1], 10);
            return `${parts[0]}.${lastDigit + 1}`;
        } else if (parts.length === 3) {
            // 3 level numbering: increment third digit
            const lastDigit = parseInt(parts[2], 10);
            return `${parts[0]}.${parts[1]}.${lastDigit + 1}`;
        }

        // Fallback: just increment the last part
        const lastDigit = parseInt(parts[parts.length - 1], 10);
        parts[parts.length - 1] = (lastDigit + 1).toString();
        return parts.join('.');
    }

    /**
     * Get the next component number based on the last used component number
     */
    private getNextComponentNumber(sectionCounter: number, lastComponentNumber: string | null): string {
        if (lastComponentNumber) {
            const parts = lastComponentNumber.split('.');
            if (parts.length >= 2) {
                const lastDigit = parseInt(parts[1], 10);
                return `${sectionCounter}.${lastDigit + 1}`;
            }
        }

        // First component in section
        return `${sectionCounter}.1`;
    }
}
